import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subWeeks,
  subMonths,
  subYears,
} from 'date-fns';
import { getFiscalMonthRange } from '../stores/fiscalMonthSettingsStore';

export type DateRangePreset =
  | 'yesterday'
  | 'today'
  | 'thisWeek'
  | 'thisMonth'
  | 'currentFiscalMonth'
  | 'previousFiscalMonth'
  | 'lastWeek'
  | 'lastMonth'
  | 'thisYear'
  | 'lastYear'
  | 'custom';

export const getDateRangeForPreset = (
  preset: DateRangePreset
): { from: string; to: string } => {
  const now = new Date();
  const today = format(now, 'yyyy-MM-dd');

  switch (preset) {
    case 'yesterday': {
      const yesterday = subDays(now, 1);
      return {
        from: format(yesterday, 'yyyy-MM-dd'),
        to: format(yesterday, 'yyyy-MM-dd'),
      };
    }
    case 'today':
      return {
        from: today,
        to: today,
      };
    case 'thisWeek': {
      const weekStart = startOfWeek(now, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
      return {
        from: format(weekStart, 'yyyy-MM-dd'),
        to: format(weekEnd, 'yyyy-MM-dd'),
      };
    }
    case 'thisMonth': {
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      return {
        from: format(monthStart, 'yyyy-MM-dd'),
        to: format(monthEnd, 'yyyy-MM-dd'),
      };
    }
    case 'currentFiscalMonth': {
      const fiscalRange = getFiscalMonthRange(now);
      return {
        from: format(fiscalRange.start, 'yyyy-MM-dd'),
        to: format(fiscalRange.end, 'yyyy-MM-dd'),
      };
    }
    case 'previousFiscalMonth': {
      const currentFiscalRange = getFiscalMonthRange(now);
      const previousFiscalDate = subDays(currentFiscalRange.start, 1);
      const previousFiscalRange = getFiscalMonthRange(previousFiscalDate);
      return {
        from: format(previousFiscalRange.start, 'yyyy-MM-dd'),
        to: format(previousFiscalRange.end, 'yyyy-MM-dd'),
      };
    }
    case 'lastWeek': {
      const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      return {
        from: format(lastWeekStart, 'yyyy-MM-dd'),
        to: format(lastWeekEnd, 'yyyy-MM-dd'),
      };
    }
    case 'lastMonth': {
      const lastMonthStart = startOfMonth(subMonths(now, 1));
      const lastMonthEnd = endOfMonth(subMonths(now, 1));
      return {
        from: format(lastMonthStart, 'yyyy-MM-dd'),
        to: format(lastMonthEnd, 'yyyy-MM-dd'),
      };
    }
    case 'thisYear': {
      const yearStart = startOfYear(now);
      const yearEnd = endOfYear(now);
      return {
        from: format(yearStart, 'yyyy-MM-dd'),
        to: format(yearEnd, 'yyyy-MM-dd'),
      };
    }
    case 'lastYear': {
      const lastYearStart = startOfYear(subYears(now, 1));
      const lastYearEnd = endOfYear(subYears(now, 1));
      return {
        from: format(lastYearStart, 'yyyy-MM-dd'),
        to: format(lastYearEnd, 'yyyy-MM-dd'),
      };
    }
    default:
      return {
        from: format(subDays(now, 7), 'yyyy-MM-dd'),
        to: today,
      };
  }
};
